import React, {ReactElement} from 'react';

import classNames from 'classnames';

import Count, {ICountProps} from '../count/count';
import {Icon} from '../icon/icon';
import {Emoticons} from '../forms';
import {Dropdown} from '../dropdown';
import {Spinner} from '../spinner';

export interface IButtonBaseProps {
    text?: string;
    ariaLabel?: string;
    block?: boolean;
    buttonRef?: (element: HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement | null) => void;
    className?: string;
    color?: 'white' | 'red' | 'green' | 'green-border' | 'red-border';
    count?: ICountProps;
    disabled?: boolean;
    hideLabel?: boolean;
    icon?: string;
    iconLeft?: string;
    iconRight?: string;
    id?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
    style?: 'link' | 'square' | 'filter' | 'circle';
    size?: TCircleSize,
    target?: string;
    type?: 'button' | 'submit' | 'reset';
    url?: string;
    showEmoticons?: boolean;
    showExpand?: boolean;
    isTextBold?: boolean
}

export type TCircleSize = 1 | 2 | 3 | 4 | 5 ;

export interface IButtonSpanProps extends IButtonBaseProps {
    attributes?: React.HTMLAttributes<HTMLSpanElement>;
    element?: 'span';
    url?: undefined;
}

export interface IButtonAnchorProps extends IButtonBaseProps {
    attributes?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
    element?: 'a';
    url: string;
}

export interface IButtonButtonProps extends IButtonBaseProps {
    attributes?: React.ButtonHTMLAttributes<HTMLButtonElement>;
    element?: 'button';
    url?: undefined;
}
export interface IButtonWithTooltipProps extends IButtonBaseProps {
    attributes?: React.ButtonHTMLAttributes<HTMLButtonElement>;
    element?: 'button';
    url?: undefined;
    tooltip: string;
    isLoading: boolean;
}

export type IButtonProps = IButtonAnchorProps | IButtonSpanProps | IButtonButtonProps | IButtonWithTooltipProps;

const Button = (props: IButtonProps): JSX.Element => {
    const className: string = classNames(
        'button',
        {
            'button--block': props.block,
            [`button--color-${props.color}`]: !!props.color,
            [`button--style-${props.style}`]: !!props.style,
            'button--style-icon': !!props.icon,
            'button--hidden-label': props.hideLabel,
        },
        props.size && `button--circle-size-${props.size}`,
        props.className,
    );

    const renderContent: () => JSX.Element = () => (
        <span className={props.isTextBold ? 'button__inner--bold' : 'button__inner'}>
            {props.icon && <Icon className="button__icon" name={props.icon} /> }
            {props.iconLeft && <Icon className="button__icon" modifier="button__icon--left" name={props.iconLeft} />}
            <span className="button__text">{props.showEmoticons ?
                <Emoticons text={props.text}/> : props.text}</span>
            {props.count && <Count {...props.count} className="button__count" size={'sm'}/>}
            {props.iconRight && <Icon className="button__icon" modifier="button__icon--right" name={props.iconRight} />}
        </span>
    );

    const renderAnchor: (anchorProps: IButtonAnchorProps) => JSX.Element = (anchorProps: IButtonAnchorProps) => (
        <a
            {...anchorProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={anchorProps.id}
            ref={anchorProps.buttonRef}
            aria-label={anchorProps.ariaLabel}
            // common attributes of a, button
            onClick={anchorProps.onClick}
            // a specific attributes
            href={anchorProps.url}
            target={anchorProps.target}
        >
            {renderContent()}
        </a>
    );

    const renderSpan: (spanProps: IButtonSpanProps) => JSX.Element = (spanProps: IButtonSpanProps) => (
        <span
            {...spanProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={spanProps.id}
            ref={spanProps.buttonRef}
            aria-label={spanProps.ariaLabel}
        >
            {renderContent()}
        </span>
    );

    const renderButton: (buttonProps: IButtonButtonProps) => JSX.Element = (buttonProps: IButtonButtonProps) => (
        <button
            {...buttonProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={buttonProps.id}
            ref={buttonProps.buttonRef}
            aria-label={buttonProps.ariaLabel}
            // common attributes of a, button
            onClick={buttonProps.onClick}
            // button specific attributes
            type={(buttonProps.attributes && buttonProps.attributes.type) || 'button'}
            disabled={buttonProps.disabled}
        >
            {renderContent()}
        </button>
    );

    if (props.element === 'span') {
        return renderSpan(props);
    }

    if (props.url) {
        if (props.disabled) {
            // type cast as a hack since we need to allow disabling anchor buttons
            // but that is actually not supported in HTML so ¯\_(ツ)_/¯
            return renderButton(props as unknown as IButtonButtonProps);
        }

        return renderAnchor(props);
    }

    return renderButton(props as IButtonButtonProps);
};

export const isToolTipButton = (object: IButtonProps): object is IButtonWithTooltipProps => 'tooltip' in object;

export const getTooltipButton = (action: IButtonProps): ReactElement => {
    if (isToolTipButton(action)) {
        if (action.isLoading) {
            return <Spinner/>;
        } else {
            return <Dropdown
                trigger={{
                    children: <Button
                        {...action}
                        style="link"
                    />,
                }}>
                {action.tooltip}
            </Dropdown>;
        }
    } else {
        return <Button {...action} style="link"/>;
    }
};

export default Button;
